import { computed, effect, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { booleanIntersects, booleanPointInPolygon, Coord } from '@turf/turf';
import { Geometry as TurfGeometry, MultiPolygon as GeoJsonMultiPolygon } from 'geojson';
import { GeoJSONSource } from 'maplibre-gl';

import CS_default_sprite from '@connect-services/client/assets/sprites/CS_default_sprite.json';
import CS_star_eau_sprite from '@connect-services/client/assets/sprites/CS_star-eau_sprite.json';
import { MapBaseSource, MapFieldInterventionSource } from '@connect-services/client/components/map/map.component';
import {
    CircleEntityProperty,
    CircleProperties,
    FilterHighlight,
    layerFillStyle,
    LayerHighlightStyle,
    layerLineStyle,
    layerPointStyle,
    LayerStyleTypeEnum,
    layerSymbolStyle,
    LineEntityProperty,
    LineProperties,
    PolygonEntityProperty,
    PolygonProperties,
    StyleProperties,
    SymbolEntityProperty,
    SymbolProperties,
} from '@connect-services/client/models/highlight-entity.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapBaseService } from '@connect-services/client/services/map-base.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import {
    Feature,
    Geometry,
    LineString,
    MapboxStyle,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point,
    Polygon,
} from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class MapHighlightEntityService extends MapBaseService {
    public static readonly isProjectChanged: WritableSignal<boolean> = signal(false);
    private static readonly SPRITES_NAMES: string[] = [
        ...Object.keys(CS_default_sprite),
        ...Object.keys(CS_star_eau_sprite),
    ];

    public readonly oldSource: WritableSignal<MapBaseSource<unknown> | null> = signal(null);
    public readonly oldSourceType: WritableSignal<string | null> = signal(null);

    private readonly homeService = inject(HomeService);
    private readonly mapModalService = inject(MapModalService);
    private readonly oldIconSize = signal(1.5);
    private readonly panelService = inject(PanelService);
    private readonly shouldResetHighlight = computed(() => {
        const isContextClosed =
            !this.panelService.isPanelOpen() &&
            !this.homeService.synopticExtPageOpen() &&
            !this.homeService.entityTableExtPageOpen();

        const modalState = this.mapModalService.state();
        const isFilterOrSelectionModalOpen =
            modalState[MapModal.FILTER].isOpen || modalState[MapModal.SELECTION].isOpen;

        return isContextClosed && !isFilterOrSelectionModalOpen;
    });

    public constructor() {
        super();
        effect(() => {
            if (!this.shouldResetHighlight()) {
                return;
            }

            this.resetHighlight(this.oldSource(), this.oldSourceType());

            if (!this.oldSource() || !this.oldSourceType()) {
                return;
            }

            this.oldSource.set(null);
            this.oldSourceType.set(null);
        });
        effect(() => {
            if (MapHighlightEntityService.isProjectChanged()) {
                this.oldSource.set(null);
                this.oldSourceType.set(null);

                MapHighlightEntityService.isProjectChanged.set(false);
            }
        });
    }

    public getEntityTypeByEntity(geom?: Geometry): LayerStyleTypeEnum {
        switch (geom?.type) {
            case MultiPoint.TypeEnum.MULTI_POINT:
            case Point.TypeEnum.POINT:
                return LayerStyleTypeEnum.SYMBOL;
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
            case LineString.TypeEnum.LINE_STRING:
                return LayerStyleTypeEnum.LINE;
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
            case Polygon.TypeEnum.POLYGON:
                return LayerStyleTypeEnum.FILL;
            default:
                return LayerStyleTypeEnum.SYMBOL;
        }
    }

    public highlightMapEntity(feature: MapGeoJSONFeatureWithLayer, source: MapBaseSource<unknown>): void {
        const sourceEntityIdKey = source instanceof MapFieldInterventionSource ? 'entityId' : 'uuid';

        AssertService.hasKey(feature.properties, sourceEntityIdKey);
        AssertService.isString(feature.properties[sourceEntityIdKey]);
        const featureId = feature.properties[sourceEntityIdKey];
        AssertService.isString(featureId);

        let featureType: LayerStyleTypeEnum = LayerStyleTypeEnum.POINT;
        switch (feature.layer.type) {
            case MapboxStyle.TypeEnum.LINE:
                featureType = LayerStyleTypeEnum.LINE;
                break;
            case MapboxStyle.TypeEnum.CIRCLE:
                featureType = LayerStyleTypeEnum.POINT;
                break;
            case MapboxStyle.TypeEnum.FILL:
                featureType = LayerStyleTypeEnum.FILL;
                break;
            case MapboxStyle.TypeEnum.SYMBOL:
                featureType = LayerStyleTypeEnum.SYMBOL;
                break;
        }

        this.resetHighlight(this.oldSource(), this.oldSourceType());
        this.manageHighlightFeature(source, [featureId], featureType);
    }

    public async highlightSource(
        source: MapBaseSource<unknown>,
        excludedIds: string[],
        entityType: LayerStyleTypeEnum,
        filterMultiPolygon?: GeoJsonMultiPolygon,
    ): Promise<void> {
        if (!this.map?.loaded()) {
            return;
        }

        const geoJsonSource: GeoJSONSource = this.map.getSource(source.sourceId) as GeoJSONSource;
        if (!geoJsonSource) {
            return;
        }

        const data = await geoJsonSource.getData();
        AssertService.assertFeatureCollectionType(data);

        const sourceEntityIdKey = source instanceof MapFieldInterventionSource ? 'entityId' : 'uuid';

        const featuresInsidePolygon = !filterMultiPolygon
            ? data.features
            : data.features.filter((feature: Feature) => {
                  try {
                      const geometryType = feature.geometry?.type;

                      if (!geometryType) {
                          return false;
                      }

                      if (geometryType === Point.TypeEnum.POINT || geometryType === MultiPoint.TypeEnum.MULTI_POINT) {
                          return booleanPointInPolygon(feature.geometry as Coord, filterMultiPolygon);
                      }

                      return booleanIntersects(feature.geometry as TurfGeometry, filterMultiPolygon);
                  } catch {
                      return false;
                  }
              });

        const filteredEntitiesId: string[] = featuresInsidePolygon
            .map((feature: Feature) => this.getEntityId(feature, sourceEntityIdKey))
            .filter((entityId: string) => !excludedIds.includes(entityId));

        if (!filteredEntitiesId.length) {
            return;
        }

        this.highlightEntities(source, filteredEntitiesId, entityType);
    }

    public manageHighlightFeature(
        source: MapBaseSource<unknown>,
        entityIds: string[],
        entityType: LayerStyleTypeEnum,
    ): void {
        this.highlightEntities(source, entityIds, entityType);

        this.oldSource.set(source);
        this.oldSourceType.set(entityType);
    }

    public resetHighlight(
        source: MapBaseSource<unknown> | null = this.oldSource(),
        entityType = this.oldSourceType(),
    ): void {
        if (!this.map?.loaded() || !entityType || !source) {
            return;
        }

        this.reset(source, entityType);
    }

    public async resetHighlightAfterMapReady(
        source: MapBaseSource<unknown> | null = this.oldSource(),
        entityType: string | null = this.oldSourceType(),
    ): Promise<void> {
        if (!source || !entityType || !this.map) {
            return;
        }

        await this.waitUntilMapReady();

        if (!this.map || !source || !entityType) {
            return;
        }

        this.reset(source, entityType);
    }

    public resetLayoutAndPaintProperties<
        T extends CircleEntityProperty | LineEntityProperty | PolygonEntityProperty | SymbolEntityProperty,
    >(
        layerId: string,
        properties: string[],
        defaultValue: (string | number)[] | undefined,
        defaultProperties?: T,
    ): void {
        if (!this.map?.getLayer(layerId)) {
            return;
        }
        properties.forEach((property, index) => {
            this.map?.setLayoutProperty(layerId, property, defaultValue ? defaultValue[index] : defaultValue);
        });

        if (!defaultProperties) {
            return;
        }

        Object.entries(defaultProperties).forEach(([property, value]) => {
            this.map?.setPaintProperty(layerId, property, value);
        });
    }

    public updateLayoutProperty(
        layerId: string,
        properties: string[],
        entityIds: string[],
        filterIdKey: string,
        propertiesValue: (string | number)[],
        defaultValue: (number | string)[] | null,
    ): void {
        if (!defaultValue || !this.map?.getLayer(layerId)) {
            return;
        }

        properties.forEach((property, index) => {
            if (!this.map?.getLayoutProperty(layerId, property)) {
                return;
            }

            this.map?.setLayoutProperty(layerId, property, [
                'match',
                ['get', filterIdKey],
                ...entityIds.flatMap((id) => [id, propertiesValue[index]]),
                defaultValue ? defaultValue[index] : defaultValue,
            ]);
        });
    }

    public updatePaintProperties<T extends Record<string, number | string | unknown[]>>(
        layerId: string,
        entityIds: string[],
        filterIdKey: string,
        highlightProperties: T,
        defaultProperties: T,
    ): void {
        if (!this.map?.getLayer(layerId)) {
            return;
        }

        Object.entries(highlightProperties).forEach(([property, value]) => {
            let defaultValue: number | null = null;

            if (typeof defaultProperties[property] === 'object') {
                defaultValue = defaultProperties?.['line-pattern'] ? 15 : 1;
            }

            this.map?.setPaintProperty(layerId, property, [
                'match',
                ['get', filterIdKey],
                ...entityIds.flatMap((id) => [id, value]),
                //use for the other entity that ar not filter
                defaultValue ?? defaultProperties[property] ?? value,
            ]);
        });
    }

    private filterHighlightByStyleId(
        source: MapBaseSource<unknown>,
        entitiesId: string[],
        entityType: LayerStyleTypeEnum,
    ): { [key: string]: FilterHighlight } {
        const styles: MapboxStyle[] = this.getSourceStyles(source, entityType);

        return styles.reduce((acc: { [key: string]: FilterHighlight }, style: MapboxStyle) => {
            acc[style.id] = {
                entitiesId,
                styleProperty: this.getStyleProperties(source, entityType, style),
            };

            return acc;
        }, {});
    }

    private getEntityId(feature: Feature, sourceEntityIdKey: string): string {
        AssertService.hasKey(feature.properties, sourceEntityIdKey);
        const entityId = feature.properties[sourceEntityIdKey];
        AssertService.isString(entityId);

        return entityId;
    }

    private getSourceStyles(source: MapBaseSource<unknown>, entityType: string): MapboxStyle[] {
        const styles = source.style.filter((styleItem: MapboxStyle) => {
            if (entityType === LayerStyleTypeEnum.LINE && styleItem.layout) {
                if (this.hasOnlyLinePattern(styleItem)) {
                    return false;
                }
            }

            if (this.isFieldInterventionCircleStyle(source, entityType, styleItem)) {
                return true;
            }

            return styleItem.type.includes(entityType);
        });

        if (!styles.length) {
            throw new Error('Style not found');
        }

        return styles;
    }

    private getStyleProperties(
        source: MapBaseSource<unknown>,
        entityType: LayerStyleTypeEnum,
        style: MapboxStyle,
    ): StyleProperties {
        if (this.isFieldInterventionCircleStyle(source, entityType, style)) {
            return new CircleProperties(style.layout!, style.paint, style.id);
        }

        switch (entityType) {
            case LayerStyleTypeEnum.POINT:
                return new CircleProperties(style.layout!, style.paint, entityType);

            case LayerStyleTypeEnum.FILL:
                return new PolygonProperties(style.layout!, style.paint, entityType);

            case LayerStyleTypeEnum.LINE:
                return new LineProperties(style.layout!, style.paint, entityType);

            case LayerStyleTypeEnum.SYMBOL:
                return new SymbolProperties(style.layout!, style.paint, style.id);

            default:
                throw new Error('Invalid source type');
        }
    }

    private hasOnlyLinePattern(styleItem: MapboxStyle): boolean {
        const paintProperties = Object.keys(styleItem.paint);

        return paintProperties.includes('line-pattern') && !paintProperties.includes('line-color');
    }

    private highlightEntities(
        source: MapBaseSource<unknown>,
        entitiesIds: string[],
        entityType: LayerStyleTypeEnum,
    ): void {
        const sourceEntityIdKey = source instanceof MapFieldInterventionSource ? 'entityId' : 'uuid';
        const filterHighlightByStyleId = this.filterHighlightByStyleId(source, entitiesIds, entityType);

        let styleConfig: LayerHighlightStyle | undefined;

        switch (entityType) {
            case LayerStyleTypeEnum.POINT:
                styleConfig = layerPointStyle;
                break;
            case LayerStyleTypeEnum.LINE:
                styleConfig = layerLineStyle;
                break;
            case LayerStyleTypeEnum.FILL:
                styleConfig = layerFillStyle;
                break;
            case LayerStyleTypeEnum.SYMBOL:
                styleConfig = layerSymbolStyle;
                break;
            default:
                styleConfig = layerPointStyle;
        }

        for (const [styleId, filterHighlight] of Object.entries(filterHighlightByStyleId)) {
            const style = source.style.find((styleItem: MapboxStyle) => styleItem.id === styleId);

            const isFieldInterventionCircleStyle = this.isFieldInterventionCircleStyle(source, entityType, style);
            const currentStyleConfig = isFieldInterventionCircleStyle ? layerPointStyle : styleConfig;

            this.updatePaintProperties(
                `${source.sourceId}-${styleId}`,
                filterHighlight.entitiesId,
                sourceEntityIdKey,
                currentStyleConfig.highlight,
                filterHighlight.styleProperty.paint,
            );

            const layoutStyleProperty: number | string | undefined =
                filterHighlight.styleProperty.layout[
                    currentStyleConfig.sortEntity as keyof typeof filterHighlight.styleProperty.layout
                ];

            if (!layoutStyleProperty) {
                continue;
            }

            const defaultValue: (number | string)[] = [layoutStyleProperty];
            const properties = [currentStyleConfig.sortEntity];

            if (isFieldInterventionCircleStyle) {
                this.updateLayoutProperty(
                    `${source.sourceId}-${styleId}`,
                    properties,
                    filterHighlight.entitiesId,
                    sourceEntityIdKey,
                    [2, 3],
                    defaultValue,
                );

                continue;
            }

            if (entityType === LayerStyleTypeEnum.SYMBOL) {
                AssertService.isSymbolLayoutProperty(filterHighlight.styleProperty.layout);

                const iconSize = filterHighlight.styleProperty.layout['icon-size'];
                const highlightValues: (number | string)[] = [2];

                if (typeof iconSize === 'number') {
                    this.oldIconSize.set(iconSize);
                    defaultValue.push(iconSize);
                    properties.push(currentStyleConfig.iconSize ?? '');
                    highlightValues.push(2);
                }

                AssertService.isString(filterHighlight.styleProperty.layout['icon-image']);
                const iconImage: string = filterHighlight.styleProperty.layout['icon-image'];

                // Change the sprite to its outline version.
                // if iconImage doesn't start with "outline" add "outline" at the beginning
                if (
                    !iconImage.startsWith('outline_') &&
                    MapHighlightEntityService.SPRITES_NAMES.includes(`outline_${iconImage}`)
                ) {
                    defaultValue.push(iconImage);
                    properties.push(currentStyleConfig.iconImage ?? '');
                    highlightValues.push(`outline_${iconImage}`);
                }

                this.updateLayoutProperty(
                    `${source.sourceId}-${styleId}`,
                    properties,
                    filterHighlight.entitiesId,
                    sourceEntityIdKey,
                    highlightValues,
                    defaultValue,
                );

                continue;
            }

            this.updateLayoutProperty(
                `${source.sourceId}-${styleId}`,
                properties,
                filterHighlight.entitiesId,
                sourceEntityIdKey,
                [2, 3],
                defaultValue,
            );
        }
    }

    private isFieldInterventionCircleStyle(
        source: MapBaseSource<unknown>,
        entityType: string,
        style?: MapboxStyle,
    ): boolean {
        return (
            source instanceof MapFieldInterventionSource &&
            entityType === LayerStyleTypeEnum.SYMBOL &&
            style?.type === MapboxStyle.TypeEnum.CIRCLE
        );
    }

    private reset(source: MapBaseSource<unknown>, entityType: string): void {
        if (!this.map?.isStyleLoaded()) {
            return;
        }

        const styles: MapboxStyle[] = this.getSourceStyles(source, entityType);

        for (const style of styles) {
            let styleProperty!: StyleProperties;

            const isFieldInterventionCircleStyle = this.isFieldInterventionCircleStyle(source, entityType, style);
            if (isFieldInterventionCircleStyle) {
                styleProperty = new CircleProperties(style.layout!, style.paint, style.id);
                this.resetLayoutAndPaintProperties(
                    `${source.sourceId}-${styleProperty.type}`,
                    ['circle-sort-key'],
                    [1],
                    styleProperty.paint,
                );

                continue;
            }

            switch (entityType) {
                case LayerStyleTypeEnum.POINT:
                    styleProperty = new CircleProperties(style.layout!, style.paint, style.id);
                    this.resetLayoutAndPaintProperties(
                        `${source.sourceId}-${styleProperty.type}`,
                        ['circle-sort-key'],
                        [1],
                        styleProperty.paint,
                    );
                    break;
                case LayerStyleTypeEnum.FILL:
                    styleProperty = new PolygonProperties(style.layout!, style.paint, style.id);
                    this.resetLayoutAndPaintProperties(
                        `${source.sourceId}-${styleProperty.type}`,
                        ['fill-sort-key'],
                        [1],
                        styleProperty.paint,
                    );
                    break;
                case LayerStyleTypeEnum.LINE:
                    styleProperty = new LineProperties(style.layout!, style.paint, style.id);
                    this.resetLayoutAndPaintProperties(
                        `${source.sourceId}-${styleProperty.type}`,
                        ['line-sort-key'],
                        [1],
                        styleProperty.paint,
                    );
                    break;
                case LayerStyleTypeEnum.SYMBOL:
                    styleProperty = new SymbolProperties(style.layout!, style.paint, style.id);
                    AssertService.isSymbolLayoutProperty(styleProperty.layout);
                    if (typeof styleProperty.layout['icon-image'] === 'string') {
                        this.resetLayoutAndPaintProperties(
                            `${source.sourceId}-${styleProperty.type}`,
                            ['symbol-sort-key', 'icon-size', 'icon-image'],
                            [1, this.oldIconSize(), styleProperty.layout['icon-image']],
                            styleProperty.paint,
                        );
                    } else {
                        this.resetLayoutAndPaintProperties(
                            `${source.sourceId}-${styleProperty.type}`,
                            ['symbol-sort-key', 'icon-size'],
                            [1, this.oldIconSize()],
                            styleProperty.paint,
                        );
                    }
                    break;
                default:
                    throw new Error('Invalid source type');
            }
        }
    }
}
